// API Configuration
export const API_CONFIG = {
  // Use environment variable or fall back to Railway production URL
  // NOTE: BASE_URL should include the /api prefix
  BASE_URL: (process.env as unknown as Record<string, string | undefined>).REACT_APP_API_BASE_URL ||
            'https://hotelvaluai-backend-production.up.railway.app/api',
  TIMEOUT: 90000, // 90 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};

// Helper function to get auth token
export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken') ||
         localStorage.getItem('token') ||
         localStorage.getItem('access_token');
};

// Helper function to get auth headers
export const getAuthHeaders = (): { Authorization: string; 'Content-Type': string } => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }

  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// ============================================================================
// API ENDPOINTS (v2.0) - Agent-Based System
// ============================================================================

export const API_ENDPOINTS = {
  // ========================================
  // DOCUMENT PROCESSING (Agent-based v2.0)
  // ========================================
  DOCUMENTS_PROCESS: '/documents/process',           // Primary upload endpoint
  DOCUMENTS_BATCH: '/documents/batch',                // Batch upload
  DOCUMENTS_HISTORY: '/documents/history',            // Document processing history
  DOCUMENTS_TYPES: '/documents/types',                // Supported document types
  DOCUMENTS_HEALTH: '/documents/health',              // Health check

  // Alternative namespace (same endpoints)
  AGENT_DOCUMENTS_PROCESS: '/agents/documents/process',
  AGENT_DOCUMENTS_BATCH: '/agents/documents/batch',
  AGENT_DOCUMENTS_HISTORY: '/agents/documents/history',

  // ========================================
  // ANALYSIS (Agent-based v2.0)
  // ========================================
  ANALYSIS_COMPREHENSIVE: '/agents/analysis/comprehensive',    // Full multi-dimensional analysis
  ANALYSIS_FOCUSED: '/agents/analysis/focused',                // Targeted analysis
  ANALYSIS_HISTORY: '/agents/analysis/history',                // Analysis history
  ANALYSIS_HEALTH: '/agents/analysis/health',                  // Health check

  // ========================================
  // CHAT (Agent-based v2.0)
  // ========================================
  AGENT_CHAT_MESSAGE: '/agents/chat/message',                  // Send message (JSON)
  AGENT_CHAT_STREAM: '/agents/chat/stream',                    // Send message (SSE streaming)
  AGENT_CHAT_CONVERSATIONS: '/agents/chat/conversations',      // List conversations
  AGENT_CHAT_HEALTH: '/agents/chat/health',                    // Health check
  AGENT_CHAT_STATS: '/agents/chat/stats',                      // Usage statistics
  AGENT_CHAT_SEARCH: '/agents/chat/search',                    // Search conversations

  // ========================================
  // MARKET INTELLIGENCE (Agent-based v2.0)
  // ========================================
  MARKET_INTELLIGENCE_ANALYZE: '/market-intelligence/:hotelId/analyze',
  MARKET_INTELLIGENCE_REPORT: '/market-intelligence/:hotelId/report',
  MARKET_INTELLIGENCE_HISTORY: '/market-intelligence/:hotelId/history',
  MARKET_INTELLIGENCE_REFRESH: '/market-intelligence/:hotelId/refresh',
  MARKET_INTELLIGENCE_COMPETITORS: '/market-intelligence/:hotelId/competitors',
  MARKET_INTELLIGENCE_BATCH: '/market-intelligence/batch-analyze',
  MARKET_INTELLIGENCE_METRICS: '/market-intelligence/metrics',
  MARKET_INTELLIGENCE_DELETE: '/market-intelligence/:hotelId',
  MARKET_INTELLIGENCE_EDIT: '/market-intelligence/:hotelId/edit',
  MARKET_INTELLIGENCE_VERSIONS: '/market-intelligence/:hotelId/versions',
  MARKET_INTELLIGENCE_VERSION: '/market-intelligence/:hotelId/version/:version',
  MARKET_INTELLIGENCE_REVERT: '/market-intelligence/:hotelId/revert/:version',
  MARKET_INTELLIGENCE_DIFF: '/market-intelligence/:hotelId/version-diff/:version1/:version2',

  // ========================================
  // REPORT GENERATION (Agent-based v2.0)
  // ========================================
  REPORTS_GENERATE: '/reports/generate',
  REPORTS_DOWNLOAD: '/reports/:reportId/download',
  REPORTS_LIST: '/reports/hotel/:hotelId',
  REPORTS_DELETE: '/reports/:reportId',

  // ========================================
  // SCENARIO MANAGEMENT
  // ========================================
  SCENARIOS_LIST: '/hotels/:hotelId/scenarios',
  SCENARIOS_CREATE: '/hotels/:hotelId/scenarios',
  SCENARIOS_UPDATE: '/hotels/:hotelId/scenarios/:scenarioId',
  SCENARIOS_DELETE: '/hotels/:hotelId/scenarios/:scenarioId',
  SCENARIOS_COMPARE: '/hotels/:hotelId/scenarios/compare',

  // ========================================
  // HOTEL RATE BENCHMARKING
  // ========================================
  HOTEL_RATES_SEARCH: '/hotel-rates/search',
  HOTEL_RATES_BENCHMARK: '/hotel-rates/:hotelId/benchmark',
  HOTEL_RATES_CACHED: '/hotel-rates/:hotelId/cached',
  HOTEL_RATES_PROVIDER: '/hotel-rates/provider',
  HOTEL_RATES_CACHE_CLEAR: '/hotel-rates/cache',

  // ========================================
  // OTHER ENDPOINTS
  // ========================================
  HOTELS: '/hotels',
  DEALS: '/deals',
  VALUATION: '/valuation',

  // ========================================
  // DEAL PIPELINE
  // ========================================
  DEALS_PIPELINE: '/deals/pipeline',
  DEALS_PIPELINE_STAGES: '/deals/pipeline/stages',
  DEALS_ANALYTICS: '/deals/analytics',
  DEALS_MOVE_STAGE: '/deals/:dealId/stage',
  DEALS_SCORE: '/deals/:dealId/score',
  DEALS_CHECKLIST: '/deals/:dealId/checklist',
  DEALS_CHECKLIST_ITEM: '/deals/:dealId/checklist/:itemId',
  DEALS_ACTIVITY: '/deals/:dealId/activity',
  DEALS_IC_MEMO: '/deals/:dealId/ic-memo',

  // ========================================
  // USALI FINANCIAL STATEMENTS
  // ========================================
  USALI_STATEMENT: '/usali/hotels/:hotelId/statement',
  USALI_T12: '/usali/hotels/:hotelId/t12',
  USALI_HISTORICAL: '/usali/hotels/:hotelId/historical',
  USALI_BUDGET: '/usali/hotels/:hotelId/budget',
  USALI_VARIANCE: '/usali/hotels/:hotelId/variance',
  USALI_SCHEDULE: '/usali/hotels/:hotelId/schedule/:scheduleNumber',

  // ========================================
  // DEBT UNDERWRITING
  // ========================================
  LOAN_SIZING: '/investment/:hotelId/loan-sizing',
  STRESS_TEST: '/investment/:hotelId/stress-test',
  LOAN_COMPARISON: '/investment/:hotelId/loan-comparison',

  // ========================================
  // WATERFALL RETURNS
  // ========================================
  WATERFALL_CALCULATE: '/investment/:hotelId/waterfall',
  WATERFALL_PRESETS: '/investment/:hotelId/waterfall/presets',
  SENSITIVITY_MATRIX: '/investment/:hotelId/sensitivity-matrix',
  LEVERED_UNLEVERED: '/investment/:hotelId/levered-unlevered-comparison',

  // ========================================
  // INSTITUTIONAL PRO FORMA
  // ========================================
  INSTITUTIONAL_PRO_FORMA: '/investment/:hotelId/institutional-pro-forma',
  PRO_FORMA_MONTHLY: '/investment/:hotelId/pro-forma/monthly/:year',
  EXIT_ANALYSIS: '/investment/:hotelId/exit-analysis',
  PRO_FORMA_CAGR: '/investment/:hotelId/pro-forma/cagr',
  RAMP_UP_PROFILES: '/investment/:hotelId/ramp-up-profiles',
  SEASONALITY_PROFILES: '/investment/:hotelId/seasonality-profiles',
  PIP_SCHEDULE: '/investment/:hotelId/pip-schedule',

  // ========================================
  // STR INTEGRATION
  // ========================================
  STR_UPLOAD: '/hotels/:hotelId/str/upload',
  STR_MANUAL: '/hotels/:hotelId/str/manual',
  STR_LATEST: '/hotels/:hotelId/str/latest',
  STR_HISTORY: '/hotels/:hotelId/str/history',
  STR_TRENDS: '/hotels/:hotelId/str/trends',
  STR_MOVING_AVERAGES: '/hotels/:hotelId/str/moving-averages',
  STR_COMPETITIVE: '/hotels/:hotelId/str/competitive-positioning',
  STR_SEASONALITY: '/hotels/:hotelId/str/seasonality',
  STR_FORECAST: '/hotels/:hotelId/str/forecast',
  STR_PIPELINE: '/hotels/:hotelId/str/supply-pipeline',
  STR_DELETE: '/hotels/:hotelId/str/:dataId',

  // ========================================
  // INSTITUTIONAL KPIs
  // ========================================
  KPI_HOTEL: '/analytics/:hotelId/kpis',
  KPI_TRENDS: '/analytics/:hotelId/kpi-trends',
  KPI_PEER_COMPARISON: '/analytics/:hotelId/peer-comparison',
  KPI_BENCHMARKS: '/analytics/benchmarks',
  KPI_OPERATING_EFFICIENCY: '/analytics/:hotelId/operating-efficiency',

  // ========================================
  // LEGACY ENDPOINTS (Migrated to v2.0)
  // ========================================
  /** @deprecated Use MARKET_INTELLIGENCE_* endpoints instead */
  MARKET_RESEARCH: '/market-research',
  /** Now uses /agents/documents namespace */
  UPLOADS: '/agents/documents',
  /** Now uses /agents/documents namespace */
  OCR: '/agents/documents',
  /** @deprecated Use AGENT_CHAT_MESSAGE instead */
  CHAT: '/chat',
  /** @deprecated Use AGENT_CHAT_CONVERSATIONS instead */
  CHAT_HISTORY: '/chat/history',
};
